<template>
    <div>
        <div class="form-input-text--label">
            <span v-if="label">{{ label }}:</span>
            <span v-if="showInfoTip">
                <common-info-tip>
                    <span>{{ infoTipMessage }}</span>
                </common-info-tip>
            </span>
        </div>
        <div v-click-away="blur" :class="inputClass">
            <div class="form-input-select__wrapper">
                <div :class="valueClass" @click="toggle()">
                    <div
                        v-if="(value && value.length) || (valuePath && formData[valuePath]?.length)"
                        class="form-input-select__labels"
                    >
                        <common-badges
                            :badges="selectedOptions"
                            @close="removeFilter"
                            @click="toggleOption($event)"
                        ></common-badges>
                    </div>

                    <span v-else>
                        {{ placeholder }}
                    </span>

                    <common-icon name="dropdown-arrow" size="medium"></common-icon>
                </div>

                <div ref="options" class="form-input-select__options">
                    <section v-for="(option, index) in multiSelectOptions" :key="`group-${index}}`">
                        <h5
                            v-if="option.heading && hasExpandableOptions"
                            class="form-input-select__options-heading"
                            :class="{ 'form-input-select__options-heading--is-open': isExpanded(option) }"
                            @click="toggleExpand(option)"
                        >
                            {{ option.heading }}

                            <common-icon name="menu-arrow" size="large"></common-icon>
                        </h5>

                        <button v-if="!option.heading" class="form-input-select__option" type="button">
                            <checkbox-input
                                :label="option.label"
                                :hasBigTapArea="true"
                                :isDisabled="isDisabled"
                                :value="isChecked(option)"
                                @change="toggleOption(option)"
                            ></checkbox-input>
                        </button>
                        <button
                            v-for="op in option.options"
                            v-else-if="hasExpandableOptions && isExpanded(option)"
                            :key="op.value"
                            class="form-input-select__option"
                            :class="{ 'form-input-select__suboption': isExpanded(option) && option.heading }"
                            type="button"
                        >
                            <checkbox-input
                                :label="op.label"
                                :hasBigTapArea="true"
                                :isDisabled="isDisabled"
                                :value="isChecked(op)"
                                @change="toggleOption(op)"
                            ></checkbox-input>
                        </button>
                    </section>
                </div>
            </div>
        </div>

        <span v-if="errorMessage" class="form-input-text--error">{{ errorMessage }}</span>
    </div>
</template>

<script>
    import FormInputMixin from 'mixins/FormInputMixin'
    import IndicateScrollMixin from 'mixins/IndicateScrollMixin'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'
    import cloneDeep from 'lodash.clonedeep'

    export default {
        mixins: [FormInputMixin, IndicateScrollMixin],

        components: {
            CheckboxInput,
        },

        props: {
            value: { type: Array, default: () => [] },
            options: { type: Array, required: true }, //example -> [{ label: 'something', value: 1 }]
            showSelectAllOption: { type: Boolean, default: false },
            hasExpandableOptions: { type: Boolean, default: false },
            showInfoTip: { type: Boolean, default: false },
            infoTipMessage: { type: String, default: '' },
        },

        data() {
            return {
                isOpen: false,
                lastSelectedOptionLabel: null,
                multiSelectOptions: [],
                expanded: [],
                isFilterRemoved: false,
            }
        },

        computed: {
            inputClass() {
                return {
                    'form-input-select': true,
                    'form-input-select--multi': true,
                    'form-input-select--is-open': this.isOpen,
                    'form-input-select--is-invalid': this.errorMessage,
                    'form-input-select--is-disabled': this.isDisabled,
                }
            },

            valueClass() {
                return {
                    'form-input-select__input': true,
                    'form-input-select__input--has-no-value': !this.value || !this.value.length,
                }
            },

            selectedOptions() {
                const values = this.valuePath && this.formData[this.valuePath] ? this.formData[this.valuePath] : this.value
                if (!this.hasExpandableOptions) {
                    return this.multiSelectOptions.filter(({ value }) => values.includes(value)) || []
                }
                const notExtandable = this.multiSelectOptions.filter((el) => !el.heading && values.includes(el.value)) || []

                const extendable =
                    this.multiSelectOptions
                        .filter((el) => el.heading)
                        .reduce((acc, el) => [...acc, ...el.options], [])
                        .filter((el) => values.includes(el.value)) || []
                return [...notExtandable, ...extendable]
            },

            allOptions() {
                return this.options.reduce((acc, option) => {
                    if (option.options) {
                        return [...acc, ...option.options.map((op) => op.value)]
                    }
                    return [...acc, option.value]
                }, [])
            },

            areAllOptionsSelected() {
                const selectedOptions = this.valuePath
                    ? this.formData[this.valuePath]
                    : this.selectedOptions.map((option) => option.value)
                return this.allOptions.every((option) => selectedOptions?.includes(option))
            },
        },

        methods: {
            isChecked({ value }) {
                if (!this.value) {
                    return false
                }
                if (value === 'ALL') {
                    return this.areAllOptionsSelected
                }
                if (this.valuePath) {
                    return this.formData[this.valuePath]?.includes(value)
                }

                return this.value.includes(value)
            },

            toggleOption({ value: newValue }) {
                if (!newValue) {
                    return
                }
                if (newValue === 'ALL') {
                    return this.toggleAllOption()
                }
                if (this.isChecked({ value: newValue })) {
                    if (this.valuePath && this.formData[this.valuePath]) {
                        return this.change(this.formData[this.valuePath].filter((existingValue) => existingValue !== newValue))
                    }
                    return this.change(this.value.filter((existingValue) => existingValue !== newValue))
                }

                if (this.value.length) {
                    this.change([...this.value, newValue])
                } else if (this.valuePath && this.formData[this.valuePath]) {
                    this.change([...this.formData[this.valuePath], newValue])
                } else {
                    this.change([newValue])
                }
            },

            toggleAllOption() {
                this.change(this.areAllOptionsSelected ? [] : this.allOptions)
            },

            blur() {
                this.isFocused = false
                this.close()
            },

            close() {
                this.isOpen = false
            },

            toggle() {
                if (this.isDisabled || this.isFilterRemoved) {
                    this.isFilterRemoved = false
                    return
                }

                this.isOpen = !this.isOpen
            },

            prefillMultiselectOptions() {
                this.multiSelectOptions = cloneDeep(this.options)
                if (!this.showSelectAllOption) {
                    return
                }

                if (this.multiSelectOptions.length > 1) {
                    this.multiSelectOptions.unshift({ label: this.$t('common.microcopy.all'), value: 'ALL' })
                }
            },

            isExpanded(group) {
                return this.expanded.includes(group.value)
            },

            toggleExpand(group) {
                if (!this.hasExpandableOptions) {
                    return
                }

                if (!this.isExpanded(group)) {
                    this.expanded.push(group.value)
                } else {
                    this.expanded = this.expanded.filter((item) => item !== group.value)
                }
            },

            removeFilter(event) {
                this.isFilterRemoved = true
                this.toggleOption({ value: event.value })
            },
        },

        watch: {
            options() {
                this.prefillMultiselectOptions()
            },
        },

        mounted() {
            this.prefillMultiselectOptions()
        },
    }
</script>
